/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:22:30 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { CurrencyPickerInput } from "core/components/CurrencyPickerInput";
import { BillingConfiguration } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { useBillingConfigurationAPIForm } from "service-builder/services";
import { TextFormInput, AutoSaveAPIForm } from "ui-widgets/forms";
import { OSAPIFormContext } from "ui-widgets/forms/providers";
import { EmphasisText, InputHelpDialogWrapper } from "ui-widgets";
import { BillingScheduleList } from "./BillingScheduleList";
import { PaymentMethodsMany2ManyCheckboxesPicker } from "./PaymentMethodsMany2ManyPicker";
import { BillingConfigurationPaymentProviderCredentialsButton } from "./BillingConfigurationPaymentProviderCredentialsButton";

interface BillingConfigurationFormProps {
  billingConfigurationId: PrimaryKeyValue<BillingConfiguration>;
}

export const BillingConfigurationForm = ({ billingConfigurationId }: BillingConfigurationFormProps) => {
  const useBillingConfigurationAPIFormReturn = useBillingConfigurationAPIForm(billingConfigurationId);
  const {
    useFormReturn: { watch },
  } = useBillingConfigurationAPIFormReturn;
  const billingCurrency = watch("currency");

  return (
    <AutoSaveAPIForm
      useAPIFormReturn={useBillingConfigurationAPIFormReturn}
      loaderType="custom"
      customProgressLoader={<BillingConfigurationFormSkeleton />}
    >
      <Stack spacing={2}>
        <OSAPIFormContext.Consumer>
          {({ useFormReturn: { watch } }) => {
            let [otherTasksCount, otherServicesCount] = watch<(keyof BillingConfiguration)[]>([
              "task_template_count",
              "process_template_count",
            ]);
            otherTasksCount = otherTasksCount - 1;
            otherServicesCount = otherServicesCount - 1;
            const showOthersAlert = otherTasksCount > 0 || otherServicesCount > 0;

            if (showOthersAlert) {
              return (
                <Alert severity="warning" variant="standard">
                  <AlertTitle>Take Note</AlertTitle>
                  {otherServicesCount ? (
                    <p>{otherServicesCount} other service(s) are using this configuration.</p>
                  ) : null}
                  {otherTasksCount ? <p>{otherTasksCount} other task(s) are using this configuration.</p> : null}
                </Alert>
              );
            }
            return null;
          }}
        </OSAPIFormContext.Consumer>

        <Stack direction="row" spacing={2}>
          <Paper sx={{ paddingLeft: 5, paddingRight: 5, paddingTop: 5, paddingBottom: 5, minWidth: "430px" }}>
            <TextFormInput<BillingConfiguration> schema_field_key="name" label="Name" />
            <TextFormInput<BillingConfiguration> schema_field_key="code" label="Code" />
            <InputHelpDialogWrapper
              helpTitle={<>Due days</>}
              helpContent={
                <>Set how many days your clients will have to pay for invoices created by this billing configuration.</>
              }
            >
              <TextFormInput<BillingConfiguration> schema_field_key="due_days" label="Due days" />
            </InputHelpDialogWrapper>
            <CurrencyPickerInput<BillingConfiguration> schema_field_key="currency" label="Transaction Currency" />
          </Paper>
          <Paper sx={{ paddingLeft: 5, paddingRight: 5, paddingTop: 2, paddingBottom: 2, minWidth: "430px" }}>
            <PaymentMethodsMany2ManyCheckboxesPicker<BillingConfiguration>
              currencies={billingCurrency ? [billingCurrency] : []}
              PaymentProviderCredentialsButtonComponent={({ paymentProvider }) => (
                <BillingConfigurationPaymentProviderCredentialsButton
                  billingConfigurationId={billingConfigurationId}
                  paymentProvider={paymentProvider}
                />
              )}
              schema_field_key="payment_methods"
              label="Choose Online Payment Methods"
              helperText={
                <p>
                  <EmphasisText>Note:</EmphasisText> If you do not select any online payment methods, you will have to
                  configure the payment task to use a manual payment form for the client to provide proof of payment.
                </p>
              }
            />
          </Paper>
        </Stack>

        <Paper sx={{ paddingLeft: 5, paddingRight: 5, paddingTop: 2, paddingBottom: 2 }}>
          <BillingScheduleList />
        </Paper>
      </Stack>
    </AutoSaveAPIForm>
  );
};

const BillingConfigurationFormSkeleton = () => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2}>
        <Skeleton animation="wave" variant="rectangular" sx={{ width: "50%" }} height={300} />
        <Skeleton animation="wave" variant="rectangular" sx={{ width: "50%" }} height={300} />
      </Stack>
      <Skeleton animation="wave" variant="rectangular" height={200} />
    </Stack>
  );
};
